import {
  ApolloClient,
  createHttpLink,
  type DefaultContext,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {onError} from '@apollo/client/link/error';
import {relayStylePagination} from '@apollo/client/utilities';
import {resolveViewerSession} from '~/lib/auth/viewer-session';
import {getViewerApiUrl, parseRegion} from '~/lib/config';

/**
 * Apollo client for the viewer schema, which lists every page shared with the
 * client at one firm. The region comes from the organization identifier's
 * prefix (API data, never the URL path).
 *
 * The viewer session is minted asynchronously, so the credential is resolved
 * inside the auth link per request rather than being passed in — mirroring the
 * main client's Firebase `authLink`. That keeps the client synchronously
 * constructible, so consumers are plain `useQuery` calls.
 *
 * Returns `{client, dispose}` like the firm client; `dispose` stops the client
 * so its watchers are released when the organization changes or unmounts.
 */
export function createViewerApolloClient(organizationIdentifier: string): {
  client: ApolloClient<NormalizedCacheObject>;
  dispose: () => void;
} {
  const region = parseRegion(organizationIdentifier);

  const httpLink = createHttpLink({
    uri: getViewerApiUrl(region),
  });

  const authLink = setContext(async (_, context: DefaultContext) => {
    const headers = context.headers as Record<string, string>;
    const session = await resolveViewerSession(organizationIdentifier);

    // No credential means the request can only 401, so fail here instead of
    // spending the round trip. The consumer renders nothing either way.
    if (!session) {
      throw new Error('No viewer session');
    }

    return {
      headers: {...headers, Authorization: `Bearer ${session.token}`},
    };
  });

  // Viewer failures are expected for clients with no grants and are never shown
  // to them, so they are swallowed rather than reported. In dev they are logged,
  // where a swallowed schema mismatch would otherwise look like an empty list.
  const errorLink = onError(({graphQLErrors, networkError}) => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[viewer] request failed', {graphQLErrors, networkError});
    }
  });

  const client = new ApolloClient({
    link: authLink.concat(errorLink).concat(httpLink),
    cache: createViewerCache(),
    connectToDevTools: import.meta.env.DEV,
    name: 'sharing-viewer',
  });

  return {client, dispose: () => client.stop()};
}

// Shared with tests so a MockedProvider merges pages the way the real client does.
export function createViewerCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      // Viewer is a singleton, so every page of canvases merges into one entry.
      Viewer: {keyFields: [], fields: {canvases: relayStylePagination()}},
      ViewerCanvas: {keyFields: ['identifier']},
    },
  });
}
